/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Radio from 'backbone.radio';
import Ladda from 'ladda';
import Syphon from 'backbone.syphon';
import Backbone from 'backbone';

import Config from '@config/Config';
import { validateForm } from '@/util/ParsleyUtils';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import Session from '@/Session';

import CACRegistrationInfoForm from '../../forms/CACRegistrationInfoForm';
import AddCommandView from '../../views/AddCommandView';
import ExternalCACRegistration from '../../services/ExternalCACRegistration';

import './CACRegisterPage.css';
import tmplCACRegisterPage from './CACRegisterPage.hbs';

// ! MR - TODO
// * Validate Command choice as required

const CACRegisterPage = View.extend({
  template: tmplCACRegisterPage,

  className: 'cac-register-page',

  ui: {
    cacRegistrationInfoForm: '.cac-registration-info-form',
    addCommandView: '.add-command-view',
    register: '.btn-register',
    btnNoCACRegister: '.btn-no-cac-register'
  },

  regions: {
    cacRegistrationInfoForm: '@ui.cacRegistrationInfoForm',
    addCommandView: '@ui.addCommandView'
  },

  events: {
    'click .btn-return-to-login': 'returnToLogin',
    'click @ui.register': 'register',
    'click @ui.btnNoCACRegister': 'onClickNoBtnCacRegister'
  },

  templateContext() {
    return {
      bannerText: Config.getBannerText()
    };
  },

  onRender() {
    this.showChildView('cacRegistrationInfoForm', new CACRegistrationInfoForm());
    this.showChildView(
      'addCommandView',
      new AddCommandView({
        model: new Backbone.Model({
          isProfilePage: false,
          isRegistrationPage: true
        })
      })
    );
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
    this.getChildView('cacRegistrationInfoForm').toggleForm(state);
    this.getChildView('addCommandView').toggleForm(state);
  },

  returnToLogin(e) {
    e.preventDefault();
    Radio.channel('navigate').request('show:ext:login');
  },

  showRegistrationError() {
    showErrorMessage({
      errorCode: 'registrationError',
      errorTitle: 'Registration Error',
      errorMessage:
        'An error occurred during registration. Please reference the requirements for the fields by selecting the question mark icon by each field. If the information you entered meets these standards, please try again or contact an administrator if your issue persists.'
    });
  },

  showRegistrationSuccess() {
    showSuccessMessage({
      code: 'registrationSuccess',
      title: 'Request Received',
      message:
        'Your request for registration has been received. An email will be sent once your request has been approved.'
    });
  },

  async register(e) {
    e.preventDefault();

    const spinner = Ladda.create(this.ui.register[0]);
    spinner.start();
    const formData = Syphon.serialize(this);

    try {
      const [, validCommand] = await Promise.all([
        validateForm(this.ui.cacRegistrationInfoForm),
        this.getChildView('addCommandView').validateCommandsList()
      ]);
      if (!validCommand) return;

      this.toggleForm(false);

      formData.commandIds = this.getChildView('addCommandView').getCommandIds();

      const userId = await ExternalCACRegistration(formData);
      Session.userId = userId;
      await this.getChildView('addCommandView').submit();

      this.showRegistrationSuccess();
      Radio.channel('navigate').request('show:ext:login');
    } catch (err) {
      this.toggleForm(true);
      if (err) {
        this.showRegistrationError();
      }
    } finally {
      spinner.stop();
    }
  },

  onClickNoBtnCacRegister(e) {
    e.preventDefault();
    Radio.channel('navigate').request('show:ext:register-page');
  }
});

export default CACRegisterPage;
